import { useEffect } from "react";
import { useDispatch, useSelector } from "react-redux";
import { RootState } from "../../store";
import Loading from "../indicators/Loading";
import Error from "../error/Error";
import {
  setCompany,
  setCompanyErrorLoading,
  setCompanyLoaded,
  setCompanyLoading,
  setUserCompanies,
} from "../../redux/company/company-slice";
import { getList } from "../../api/generics";
import Company from "../../models/company";
import { SUCCESS } from "../../utils/constants/tags";
import { getCompanyId } from "../../local-config/company";

interface AppDataLoadedFirstProps {
  children: React.ReactNode;
}

const COMPANY_LIMIT = 1000;

const AppDataLoadedFirst: React.FC<AppDataLoadedFirstProps> = ({
  children,
}) => {
  const dispatch = useDispatch();
  const loggedIn = useSelector((state: RootState) => state.user.loggedIn);
  const companyLoaded = useSelector((state: RootState) => state.company.loaded);

  const companyLoading = useSelector(
    (state: RootState) => state.company.loading
  );

  const companyError = useSelector((state: RootState) => state.company.error);

  const companyPermissionsLoading = useSelector(
    (state: RootState) => state.companyPermissions.loading
  );

  const companyAdditionalFieldsLoading = useSelector(
    (state: RootState) => state.companyAdditionalFields.loading
  );

  const loadData = async () => {
    if (companyLoading || companyLoaded) {
      return;
    }

    dispatch(setCompanyLoading(true));

    const companiesRequestStatus = await getList<Company>(
      "/companies/for_user/",
      COMPANY_LIMIT,
      0
    );

    if (
      companiesRequestStatus.status !== SUCCESS ||
      companiesRequestStatus.data === undefined
    ) {
      dispatch(setCompanyErrorLoading(true));
      return;
    }

    const companyId = getCompanyId();

    let found = false;
    let selectedCompany: Company | null = null;
    for (const company of companiesRequestStatus.data.items) {
      if (company.id === companyId) {
        found = true;
        selectedCompany = company;
        break;
      }
    }

    if (!found && companiesRequestStatus.data.items.length > 0) {
      selectedCompany = companiesRequestStatus.data.items[0];
    } else if (!found) {
      dispatch(setCompanyErrorLoading(true));
      return;
    }

    dispatch(setCompany(selectedCompany!));
    dispatch(setUserCompanies(companiesRequestStatus.data.items));
  };

  useEffect(() => {
    loadData();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  useEffect(() => {
    if (!loggedIn) {
      dispatch(setCompany({}));
      dispatch(setCompanyLoading(false));
      dispatch(setCompanyLoaded(false));
      dispatch(setCompanyErrorLoading(false));
    } else {
      loadData();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [loggedIn]);

  if (!companyLoaded) {
    return <Loading />;
  }

  if (companyPermissionsLoading || companyAdditionalFieldsLoading) {
    return <Loading />;
  }

  if (companyError) {
    return <Error />;
  }

  return <>{children}</>;
};

export default AppDataLoadedFirst;
